import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";

/**
 * Iter-562 §5 — accidental back/exit recovery: EngageSessionPanel
 * persists the last claimed session ({qr, store, at}) in localStorage;
 * this site-wide ribbon offers "🔄 Back to your session at {store}".
 * Re-opening /engage/{qr} re-claims the live session and the wizard
 * resumes at the right step (incl. Pay). Entries older than the 3 h
 * session TTL are ignored. Dismissal is per-entry, so a NEW session
 * resurfaces the ribbon.
 */
const TTL_MS = 3 * 60 * 60 * 1000;

export default function BackToSessionRibbon() {
  const location = useLocation();
  const [dismissedKey, setDismissedKey] = useState(
    () => sessionStorage.getItem("back_session_dismissed") || "",
  );
  let entry = null;
  try {
    entry = JSON.parse(localStorage.getItem("engage_last_session") || "null");
  } catch {
    entry = null;
  }
  const fresh = entry?.qr && Date.now() - (entry.at || 0) < TTL_MS;
  const key = fresh ? `${entry.qr}:${entry.at}` : "";
  if (!fresh || dismissedKey === key || location.pathname.startsWith("/engage/")) return null;

  return (
    <div className="bg-slate-900 text-white" data-testid="back-session-ribbon">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-2 flex items-center gap-3 text-sm">
        <Link
          to={`/engage/${entry.qr}`}
          data-testid="back-session-link"
          className="flex-1 font-semibold hover:underline truncate"
        >
          🔄 Back to your session at {entry.store || "the store"}
        </Link>
        <button
          type="button"
          aria-label="Dismiss"
          data-testid="back-session-dismiss"
          onClick={() => {
            sessionStorage.setItem("back_session_dismissed", key);
            setDismissedKey(key);
          }}
          className="shrink-0 grid place-items-center w-6 h-6 rounded-full hover:bg-white/20 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
